import { FieldDef, FieldDefaults } from "../../model/qubes/field";
import { KIND_OPTIONS, KIND_SCHEDULED } from "./renderer/payload-model";
import { LOG_PARSER_LABELS } from "../log-viewer/types";
import { parsersForChannel } from "../log-viewer/channels";

// A concise FieldDef factory: spreads the shared FieldDefaults (getValue /
// getFormItem / getDisplayComponent) so authoring a field is just its
// name / type / label / flags — the behaviour comes from the defaults.
export function jf(def: Partial<FieldDef> & { name: string }): FieldDef {
  return { ...FieldDefaults, ...def } as FieldDef;
}

// ── The Job model: a NAMED MAP of fields ────────────────────────────────────
// Reference a field by OBJECT at the call site — JobDef.concurrency_policy — so
// there are no field-name strings in the form API (autocomplete + rename-safe).
// The internal name:"..." is the db column and stays a string (fine for now).
//
// This file is UI-free (only depends on FieldDef) so it can be imported by both
// the form setup (job-form.ts) AND a lightweight standalone demo without pulling
// in the dataset/adminRpc/modal chain.
export const JobDef = {
  name: jf({ name: "name", label: "Name", type: "string", required: true }),
  description: jf({ name: "description", label: "Description", type: "string" }),
  // job.kind, the column the runner reads to decide WHO keeps this job running: the
  // scheduler on a timetable, or the convergence loop continuously. Only the two kinds a
  // person defines are offered — "service" is written by each job-runner registering
  // itself and "monitor-probe" by the monitors, and hand-making one of those would forge
  // a row the system believes it owns. Defaults to scheduled, which is what a job was
  // before this field existed.
  kind: jf({
    name: "kind",
    label: "Kind",
    type: "enum",
    required: true,
    getValue: (r: Record<string, unknown>) => (r["kind"] as string | undefined) ?? KIND_SCHEDULED,
    enumValues: KIND_OPTIONS.map((o) => ({ value: o.value, text: o.text })),
  }),
  // NB: the job table's worker FK column is `workeruid` (NO underscore) — a legacy
  // quirk; fileset_uid / payload_json etc. all DO use underscores.
  worker_uid: jf({ name: "workeruid", label: "Worker", type: "enum", required: true }),
  fileset_uid: jf({ name: "fileset_uid", label: "Fileset", type: "enum" }),
  payload_json: jf({ name: "payload_json", label: "What to run", type: "json" }),
  concurrency_policy: jf({
    name: "concurrency_policy",
    label: "Policy",
    type: "enum",
    required: true,
    enumValues: [
      { value: "overlap", text: "Overlap — allow concurrent runs" },
      { value: "wait", text: "Wait — skip while a run is in flight" },
    ],
  }),
  wait_completion_policy: jf({
    name: "wait_completion_policy",
    label: "Completion policy",
    type: "enum",
    enumValues: [
      { value: "next_tick", text: "Next tick — catch the next scheduled run" },
      { value: "run_immediately", text: "Run immediately — fire once the previous finishes" },
    ],
  }),
  max_attempts: jf({ name: "max_attempts", label: "Max attempts", type: "number" }),
  // The per-job default log parser — the "set it once and forget it" layer. Backed by
  // the firewall lens column `logparser` (@json_lens_str job.extra_data.logParser) —
  // reads + writes go through the SAME dbaccess path as every other column, the write
  // merging back into extra_data without clobbering siblings. Defaults to "structured".
  //
  // Deliberately NOT channel-scoped: a job is configured before anyone knows which
  // channels its runs will write (and one run can write several). The viewer applies
  // this wherever the channel admits it and defers to the channel where it does not —
  // see resolveParserType / channels.ts.
  //
  // Offers exactly the parsers a job default can actually govern — the console set,
  // i.e. the text parsers. journal/eventlog ARE in it: a buffer that declares no source
  // (plain captured output) is parsed with the job's choice, which is how a systemd job
  // pins 'journal' for output that arrives as journald JSON without a source tag.
  // 'applog' is NOT offered: an applog buffer carries fields, not text, and is always
  // routed by its source — selecting it here could never do anything.
  logparser: jf({
    name: "logparser",
    label: "Log parser",
    type: "enum",
    getValue: (r: Record<string, unknown>) => (r["logparser"] as string | undefined) ?? "structured",
    enumValues: parsersForChannel("console").map((v) => ({
      value: v,
      text: LOG_PARSER_LABELS[v],
    })),
  }),
};
